import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/api.service';
import './Gallery.css';

const Gallery = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const activeCategory = useMemo(
    () => categories.find((category) => category.slug === categorySlug),
    [categories, categorySlug]
  );

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPhotos();
    }, 250);

    return () => clearTimeout(timer);
  }, [categorySlug, search]);

  useEffect(() => {
    if (window.$) {
      window.$('[data-fancybox]').fancybox({
        buttons: ['zoom', 'slideShow', 'thumbs', 'close']
      });
    }
  }, [photos]);

  useEffect(() => {
    updateSeo(activeCategory);
  }, [activeCategory]);

  const fetchCategories = async () => {
    try {
      const response = await apiService.getCmsGalleryCategories();
      setCategories(response.data || []);
    } catch (err) {
      console.error('Error fetching CMS gallery categories:', err);
    }
  };

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: 1,
        pageSize: 100
      };

      if (categorySlug) {
        params.categorySlug = categorySlug;
      }

      if (search.trim()) {
        params.search = search.trim();
      }

      const response = await apiService.getCmsGalleryPhotos(params);
      setPhotos(response.data.items || []);
    } catch (err) {
      console.error('Error fetching CMS gallery photos:', err);
      setError('Unable to load gallery photos right now. Please try again later.');
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (event) => {
    const nextSlug = event.target.value;
    navigate(nextSlug ? `/gallery/${nextSlug}` : '/gallery');
  };

  const updateSeo = (category) => {
    const title = category?.seoTitle || category?.name || 'Photo Gallery';
    const description =
      category?.seoDescription ||
      category?.description ||
      'Browse photo highlights from SSVASS exhibitions and events.';
    const canonical = category?.seoCanonicalUrl || `${window.location.origin}${window.location.pathname}`;

    document.title = `${title} | SSVASS`;
    setMetaTag('description', description);
    setMetaTag('og:title', `${title} | SSVASS`, 'property');
    setMetaTag('og:description', description, 'property');
    setCanonical(canonical);
  };

  const setMetaTag = (name, content, attribute = 'name') => {
    let tag = document.querySelector(`meta[${attribute}="${name}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attribute, name);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };

  const setCanonical = (href) => {
    let tag = document.querySelector('link[rel="canonical"]');
    if (!tag) {
      tag = document.createElement('link');
      tag.setAttribute('rel', 'canonical');
      document.head.appendChild(tag);
    }
    tag.setAttribute('href', href);
  };

  return (
    <>
      {/* Page Header Start */}
      <div className="page-header">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2>{activeCategory?.name || 'Gallery'}</h2>
            </div>
            <div className="col-12">
              <a href="/">Home</a>
              <a href="/gallery">Gallery</a>
            </div>
          </div>
        </div>
      </div>
      {/* Page Header End */}

      {/* Gallery Start */}
      <div className="portfolio gallery-section">
        <div className="container">
          <div className="section-header text-center">
            <p>Our Gallery</p>
            <h2>{activeCategory?.seoTitle || activeCategory?.name || 'Memories from SSVASS Exhibitions'}</h2>
            {activeCategory?.description && <span className="gallery-section-description">{activeCategory.description}</span>}
          </div>

          <div className="gallery-filter-card">
            <div className="gallery-search-field">
              <i className="fas fa-search"></i>
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search photos by title or caption"
              />
            </div>
            <select value={categorySlug || ''} onChange={handleCategoryChange} aria-label="Filter gallery by category">
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.slug} value={category.slug}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {error && <div className="gallery-alert">{error}</div>}

          {loading ? (
            <div className="gallery-state">Loading gallery photos...</div>
          ) : photos.length === 0 ? (
            <div className="gallery-state">No photos found for this selection.</div>
          ) : (
            <div className="row portfolio-container">
              {photos.map((photo) => (
                <div key={photo.slug} className="col-lg-4 col-md-6 col-sm-12 portfolio-item">
                  <article className="portfolio-wrap gallery-card">
                    <a href={photo.imageUrl} data-fancybox="gallery" data-caption={photo.caption || photo.title}>
                      <img src={photo.imageUrl} alt={photo.altText || photo.title} className="img-fluid" />
                      <div className="portfolio-text gallery-card-text">
                        <span>{photo.categoryName}</span>
                        <h3>{photo.title}</h3>
                        {photo.caption && <p>{photo.caption}</p>}
                      </div>
                    </a>
                  </article>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Gallery End */}
    </>
  );
};

export default Gallery;
